// The top-level asset-class navigation in the header: one key per class
// (Stocks, Stablecoins, U.S. Treasuries, …), each a real link to its page,
// the current class lit. Names only, by request: the class values, the
// all-class total and its overlap note live on the All classes view and in
// methodology, not on every page. At phone width the switch scrolls inside
// its own frame, never the page.

import { useEffect, useRef, type KeyboardEvent } from "react";

import { ASSET_CLASSES, classHref, type AssetClassId } from "../classes";
import styles from "./ClassSwitch.module.css";

/** `current` is null on the cross-class overview, where no single class is lit. */
export function ClassSwitch({ current }: { current: AssetClassId | null }) {
  const navRef = useRef<HTMLElement>(null);

  // When the switch scrolls (phone width), start with the lit key in view.
  useEffect(() => {
    const nav = navRef.current;
    const lit = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!nav || !lit) return;
    const left = lit.offsetLeft;
    const right = left + lit.offsetWidth;
    if (left < nav.scrollLeft) nav.scrollLeft = left;
    else if (right > nav.scrollLeft + nav.clientWidth) nav.scrollLeft = right - nav.clientWidth;
  }, [current]);

  // Arrow keys move between keys; Tab still leaves the switch.
  function onKeyDown(event: KeyboardEvent<HTMLElement>): void {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    const links = [...event.currentTarget.querySelectorAll<HTMLAnchorElement>("a")];
    const at = links.indexOf(document.activeElement as HTMLAnchorElement);
    if (at === -1) return;
    event.preventDefault();
    const next =
      event.key === "Home" ? 0 : event.key === "End" ? links.length - 1 : (at + (event.key === "ArrowRight" ? 1 : -1) + links.length) % links.length;
    links[next]?.focus();
  }

  return (
    <div className={styles.wrap}>
      <nav aria-label="Asset class" className={styles.switch} onKeyDown={onKeyDown} ref={navRef}>
        {ASSET_CLASSES.map((cls) => (
          <a aria-current={cls.id === current ? "page" : undefined} className={styles.key} href={classHref(cls)} key={cls.id}>
            <span className={styles.keyLabel}>
              <span className={styles.full}>{cls.label}</span>
              <span aria-hidden className={styles.short}>
                {cls.short}
              </span>
            </span>
          </a>
        ))}
      </nav>
    </div>
  );
}
